import { DateTime } from 'luxon';
import { decodeSkipped, type DeliveryDateBasis } from './db/DeliveryDateBasis.js';
import {
  addWorkingDays,
  BERLIN,
  CUTOFF_HOUR,
  isCompanyShutdown,
  isNrwHoliday,
  previousWorkingDay,
} from './working-days.js';

/**
 * Die Herleitung des Wunschlieferdatums in Sätzen — für den Bereich „Nachvollziehen".
 *
 * Rein und ohne Lit, damit die Formulierungen prüfbar sind: Ein Termin, den niemand erklären
 * kann, wird im Zweifel überschrieben, und eine falsche Erklärung ist schlimmer als gar keine.
 * Die Oberfläche setzt die Schritte nur noch zusammen.
 */

export interface ErklaerungsSchritt {
  /** Worum es in diesem Schritt geht, z. B. „Nächster Abfahrtstag". */
  titel: string;
  /** Das Ergebnis des Schritts, z. B. „Donnerstag, 17.09.2026". */
  wert: string;
  /** Warum es so kam — je ein Satz. */
  hinweise: string[];
}

const WOCHENTAGE = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];
const KURZ = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

const tag = (iso: string) => DateTime.fromISO(iso, { zone: BERLIN });

/** `2026-09-17` → `Donnerstag, 17.09.2026`. */
export function langesDatum(iso: string): string {
  const d = tag(iso);
  if (!d.isValid) return iso;
  return `${WOCHENTAGE[d.weekday - 1]}, ${String(d.toFormat('dd.MM.yyyy'))}`;
}

/** `2026-09-17` → `Do 17.09.` — für Aufzählungen innerhalb eines Satzes. */
export function kurzesDatum(iso: string): string {
  const d = tag(iso);
  if (!d.isValid) return iso;
  return `${KURZ[d.weekday - 1]} ${String(d.toFormat('dd.MM.'))}`;
}

/** Setzt den Schlusspunkt — aber nicht hinter ein Datumskürzel, das schon auf einem endet. */
function satz(text: string): string {
  return text.endsWith('.') ? text : `${text}.`;
}

function aufzaehlung(teile: string[]): string {
  if (teile.length <= 1) return teile[0] ?? '';
  return `${teile.slice(0, -1).join(', ')} und ${teile.at(-1)}`;
}

/** Warum ein Tag nicht zählt. Ein Wochenende ist kein Feiertag, sagt aber dasselbe Ergebnis. */
function grundFuerRuhetag(d: DateTime): string {
  if (isCompanyShutdown(d)) return 'Betriebsruhe';
  if (isNrwHoliday(d)) return 'Feiertag';
  return 'Wochenende';
}

/**
 * Die Tage zwischen Abfahrt und Lieferung, aufgeteilt in die gezählten Werktage und das, was
 * dazwischen übersprungen wurde. Das ist der Kern der Frage „warum erst Montag?".
 */
export function transportSchritte(abfahrt: string, werktage: number): { gezaehlt: string[]; uebersprungen: string[] } {
  const gezaehlt: string[] = [];
  const uebersprungen: string[] = [];
  let d = tag(abfahrt);
  for (let i = 0; i < werktage; i += 1) {
    const ziel = addWorkingDays(d, 1);
    // Alles zwischen dem aktuellen Tag und dem nächsten Werktag wurde übersprungen.
    for (let zwischen = d.plus({ days: 1 }); zwischen < ziel; zwischen = zwischen.plus({ days: 1 })) {
      uebersprungen.push(`${kurzesDatum(zwischen.toFormat('yyyy-MM-dd'))} (${grundFuerRuhetag(zwischen)})`);
    }
    gezaehlt.push(kurzesDatum(ziel.toFormat('yyyy-MM-dd')));
    d = ziel;
  }
  return { gezaehlt, uebersprungen };
}

const SKIP_TEXT: Record<string, string> = {
  holiday: 'ist ein Feiertag',
  shutdown: 'fällt in die Betriebsruhe',
  cutoff: 'war nicht mehr zu halten — der Annahmeschluss lag davor',
};

const FALLBACK_TEXT: Record<string, string> = {
  'kein-debitor': 'Zu dieser Anfrage wurde kein Debitor erkannt.',
  'firma-unbekannt': 'Die Firma steht nicht im Kundenstamm.',
  'firma-ohne-zone': 'Für diese Firma ist keine Transportzone hinterlegt.',
  'zone-ohne-fahrplan': 'Zu dieser Transportzone sagt der Routenfahrplan nichts — er führt nur die deutschen Zonen.',
  'zone-inaktiv': 'Diese Transportzone ist nicht mehr aktiv.',
  'zone-ohne-transportzeit': 'Zu dieser Transportzone ist keine brauchbare Transportzeit hinterlegt.',
  'zone-ohne-abfahrtstag': 'Zu dieser Transportzone ist kein Abfahrtstag hinterlegt.',
  'kein-abfahrtstag-im-horizont': 'In den nächsten 90 Tagen fährt zu dieser Zone kein Transport.',
};

/** Der Zeitpunkt, ab dem gerechnet wurde — der Anfang jeder Herleitung. */
function schrittStart(basis: DeliveryDateBasis): ErklaerungsSchritt[] {
  if (!basis.calculatedAt) return [];
  const start = tag(basis.calculatedAt);
  return [
    {
      titel: 'Gerechnet ab',
      wert: start.isValid
        ? `${langesDatum(basis.calculatedAt)} um ${String(start.toFormat('HH:mm'))} Uhr`
        : basis.calculatedAt,
      hinweise: [
        'Dieser Zeitpunkt entscheidet, welcher Abfahrtstag noch erreichbar ist.',
        'Gerechnet wird ab der Verarbeitung, nicht ab dem Eingang der Mail — eine Anfrage, die länger liegen bleibt, bekäme sonst einen Termin in der Vergangenheit.',
      ],
    },
  ];
}

/** Kunde ohne Fahrplan: warum keiner griff, und die drei Werktage. */
function schritteErsatzregel(basis: DeliveryDateBasis): ErklaerungsSchritt[] {
  const werktage = basis.transitDays ?? 3;
  const schritte: ErklaerungsSchritt[] = [
    {
      titel: 'Kein Routenfahrplan',
      wert: basis.transportZone ? `Zone ${basis.transportZone}` : 'Keine Transportzone',
      hinweise: [
        FALLBACK_TEXT[basis.fallbackReason ?? ''] ?? 'Für diesen Kunden greift kein Routenfahrplan.',
        `Es gilt deshalb die Ersatzregel: Verarbeitungstag plus ${werktage} Werktage.`,
      ],
    },
  ];
  if (!basis.calculatedAt) return schritte;

  const { gezaehlt, uebersprungen } = transportSchritte(tag(basis.calculatedAt).toFormat('yyyy-MM-dd'), werktage);
  schritte.push({
    titel: 'Ersatzregel gerechnet',
    wert: `${gezaehlt.length} Werktage`,
    hinweise: [
      satz(`Gezählt wurden ${aufzaehlung(gezaehlt)}`),
      ...(uebersprungen.length ? [satz(`Übersprungen: ${aufzaehlung(uebersprungen)}`)] : []),
    ],
  });
  return schritte;
}

/** Selbstabholer: keine Fahrt, keine Transportzeit — nur die 12-Uhr-Regel. */
function schritteAbholer(basis: DeliveryDateBasis): ErklaerungsSchritt[] {
  return [
    {
      titel: 'Transportzone',
      wert: [basis.transportZone, basis.zoneLabel].filter(Boolean).join(' — '),
      hinweise: [
        'Diese Zone ist im Routenfahrplan als Selbstabholer geführt: Der Kunde holt selbst ab, es gibt keine Transportzeit.',
        'Vor 12 Uhr steht die Ware am nächsten Tag bereit, danach am übernächsten — fällt der auf ein Wochenende, einen Feiertag oder die Betriebsruhe, entsprechend später.',
      ],
    },
  ];
}

/** Der Regelfall: Zone, Fahrplan, Abfahrtstag samt Annahmeschluss, Transportzeit. */
function schritteFahrplan(basis: DeliveryDateBasis): ErklaerungsSchritt[] {
  const schritte: ErklaerungsSchritt[] = [
    {
      titel: 'Transportzone',
      wert: [basis.transportZone, basis.zoneLabel].filter(Boolean).join(' — '),
      hinweise: ['Sie steht am Kunden im Stammsatz und bestimmt, an welchen Tagen gefahren wird.'],
    },
  ];

  if (basis.departureWeekdays?.length)
    schritte.push({
      titel: 'Routenfahrplan',
      wert: aufzaehlung(basis.departureWeekdays.map(w => WOCHENTAGE[w - 1] ?? String(w))),
      hinweise: [
        basis.departureWeekdays.length === 5
          ? 'Zu dieser Zone wird an jedem Werktag gefahren.'
          : 'Nur an diesen Tagen verlässt eine Sendung das Haus.',
      ],
    });

  if (!basis.departureDate) return schritte;

  const annahmeschluss = previousWorkingDay(tag(basis.departureDate));
  schritte.push({
    titel: 'Nächster erreichbarer Abfahrtstag',
    wert: langesDatum(basis.departureDate),
    hinweise: [
      `Annahmeschluss dafür war ${langesDatum(annahmeschluss.toFormat('yyyy-MM-dd'))} um ${CUTOFF_HOUR}:00 Uhr — bis 12 Uhr am Werktag vor der Abfahrt.`,
      ...decodeSkipped(basis.skipped).map(
        s => `${langesDatum(s.date)} fiel aus: ${SKIP_TEXT[s.reason] ?? 'nicht möglich'}.`,
      ),
    ],
  });

  if (!basis.transitDays) return schritte;

  const { gezaehlt, uebersprungen } = transportSchritte(basis.departureDate, basis.transitDays);
  schritte.push({
    titel: 'Transportzeit',
    wert: `${basis.transitDays * 24} Stunden = ${basis.transitDays} ${basis.transitDays === 1 ? 'Werktag' : 'Werktage'}`,
    hinweise: [
      satz(`Gezählt wurden ${aufzaehlung(gezaehlt)}`),
      ...(uebersprungen.length ? [satz(`Übersprungen: ${aufzaehlung(uebersprungen)}`)] : []),
    ],
  });
  return schritte;
}

/** Der letzte Schritt: das Ergebnis, und ob es gerechnet oder gesetzt wurde. */
function schrittErgebnis(lieferdatum: string, handeingabe?: string | null): ErklaerungsSchritt {
  return {
    titel: 'Wunschlieferdatum',
    wert: langesDatum(lieferdatum),
    hinweise: handeingabe
      ? [
          'Von Hand gesetzt — diese Eingabe schlägt die Berechnung und wird von einer erneuten Extraktion nicht überschrieben.',
          `Der Routenfahrplan hätte ${langesDatum(handeingabe)} ergeben.`,
        ]
      : ['Dieser Tag geht als REQ_DATE_H an SAP.'],
  };
}

/**
 * Die vollständige Herleitung, Schritt für Schritt.
 *
 * `lieferdatum` kommt von außen, weil es eine Handeingabe sein kann — dann erklärt der letzte
 * Schritt genau das und nicht die Rechnung, die überschrieben wurde.
 */
export function erklaereLieferdatum(
  basis: DeliveryDateBasis | null | undefined,
  lieferdatum: string | null | undefined,
  handeingabe?: string | null,
): ErklaerungsSchritt[] {
  if (!basis?.source || !lieferdatum) return [];

  const mitte =
    basis.source === 'ersatzregel'
      ? schritteErsatzregel(basis)
      : basis.source === 'abholer'
        ? schritteAbholer(basis)
        : schritteFahrplan(basis);

  return [...schrittStart(basis), ...mitte, schrittErgebnis(lieferdatum, handeingabe)];
}
